import { ReactNode, useEffect, useRef, useState } from 'react';
import {
  CiteKnowledge,
  getSourceKnowledgeUrl,
  SaveKnowledge,
  SourceKnowledge,
  SupplementalKnowledgeAiReplyKnowledge,
} from 'services/api/supplementKnowledge';
import { ImageAnswer, VideoAnswer } from '../AnswerCell';
import { More } from 'views/Setting/AutoEmit/Refund/components/Task';
import { DeleteFilled, EditFilled, EllipsisOutlined, MinusCircleFilled } from '@ant-design/icons';
import { Button, Popover, Progress, Tooltip } from 'antd';
import { openExternalURL } from 'utils/openExternalURL';
import dayjs from 'dayjs';
import styled from '@emotion/styled';
import ItemList from './ItemList';
import { T, useTranslate } from '@tolgee/react';
import TransComponent from 'components/TransComponent';
import { useMutation } from '@tanstack/react-query';
import { eventBus } from 'services/eventBus';
import { formatNumber } from 'utils/numberFormat';
import {
  isSameKnowledge,
  resolveKnowledgeType,
  isNewProductKnowledge,
  usesDisableOperation,
} from './knowledgeType';
import CitationScope from './CitationScope';

export function useGetKnowledgeContent() {
  const { t } = useTranslate();

  const getKnowledgeContent = (
    knowledge: Pick<
      SupplementalKnowledgeAiReplyKnowledge,
      'key' | 'value' | 'bindItem' | 'deleted' | 'scope'
    >,
    render: (title: ReactNode, content: ReactNode, items: ReactNode) => JSX.Element,
    isSelf?: boolean,
    index?: number,
    translatedMap?: Partial<Record<string, string>>,
  ) => {
    const { key, value, deleted, scope } = knowledge;
    // New product citation snapshots may omit the legacy item bindings.
    const bindItem = knowledge.bindItem ?? [];
    const displayKey = translatedMap?.[key] || key;
    const title = (
      <h3 title={displayKey}>
        {typeof index === 'number' && (
          <span>
            【{t('知识')} {index + 1}】
          </span>
        )}
        {displayKey}
      </h3>
    );

    const texts: ReactNode[] = [];
    const medias: ReactNode[] = [];

    value.forEach((answer, answerIndex) => {
      if (answer.msgType === 'TEXT') {
        const text = translatedMap?.[answer.message || ''] || answer.message;
        texts.push(
          deleted ? (
            <Text key={`text-${answerIndex}`} title={text}>
              <del>
                {isSelf && (
                  <Tooltip title={t('这是您针对当前买家问题添加的知识')}>
                    <b>{t('添加知识')}</b>
                  </Tooltip>
                )}
                {text}
              </del>
            </Text>
          ) : (
            <Text key={`text-${answerIndex}`} title={text}>
              {isSelf && (
                <Tooltip title={t('这是您针对当前买家问题添加的知识')}>
                  <b>{t('添加知识')}</b>
                </Tooltip>
              )}
              {text}
            </Text>
          ),
        );
      }
      if (answer.msgType === 'IMAGE') {
        medias.push(
          <ImageAnswer
            key={`image-${answerIndex}`}
            answer={answer}
            ellipsis={false}
            textClassName="!text-xs !text-[var(--fett-text-tertiary)]"
          />,
        );
      }
      if (answer.msgType === 'VIDEO') {
        medias.push(
          <VideoAnswer
            key={`video-${answerIndex}`}
            answer={answer}
            ellipsis={false}
            textClassName="!text-xs !text-[var(--fett-text-tertiary)]"
          />,
        );
      }
    });

    const content = [...texts, medias.length > 0 && <Media key="medias">{medias}</Media>];

    const itemText = `${t('single_x_spu', { x: bindItem.length })} ${t('single_x_sku', {
      x: bindItem.reduce((res, { skus }) => res + skus.length, 0),
    })}`;

    const items = (
      <dl>
        <dt>{t('knowledge.应用商品')}：</dt>
        <dd>
          {scope ? (
            <CitationScope scope={scope} />
          ) : bindItem.length === 0 ? (
            itemText
          ) : (
            <Popover
              trigger="hover"
              placement="topRight"
              content={<ItemList bindItem={bindItem} />}
            >
              <More>
                {itemText}
                <span>
                  <EllipsisOutlined />
                </span>
              </More>
            </Popover>
          )}
        </dd>
      </dl>
    );

    return render(title, content, items);
  };

  return {
    t,
    getKnowledgeContent,
  };
}

function Knowledge({
  changed,
  index,
  caseId,
  knowledge,
  sourceKnowledge,
  updatedKnowledges,
  applySkuIds,
  translatedMap,
  onEdit,
  onDelete,
}: {
  changed?: boolean;
  index: number;
  caseId?: number;
  knowledge: CiteKnowledge;
  sourceKnowledge: SourceKnowledge;
  updatedKnowledges?: SaveKnowledge[];
  applySkuIds: string[];
  translatedMap: Partial<Record<string, string>>;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const { score: _score, source, updateTime } = knowledge;
  const { knowledgeId, syncTime, updateUser, sourceName, sourceUrl, deleted, modify } =
    sourceKnowledge;

  const knowledgeType = resolveKnowledgeType(knowledge);
  const isNewProduct = isNewProductKnowledge(knowledge);
  const stopOperation = usesDisableOperation(knowledge);
  const disabled = sourceKnowledge.enabled === false;

  const score = Math.max(Number((Number(_score) * 100).toFixed(2)), 1);

  const time = isNewProduct
    ? sourceKnowledge.updateTime
    : source === '商品详情页' || source === '商家外部知识导入' || source === '商家线上文档同步'
    ? syncTime
    : updateTime;

  const updatedKnowledge =
    updatedKnowledges?.find((_) =>
      _.bindItem.some(({ skus }) => skus.some(({ skuId }) => applySkuIds.includes(skuId))),
    ) || updatedKnowledges?.find((_) => isSameKnowledge(_, sourceKnowledge));

  const curKnowledge = updatedKnowledge
    ? {
        ...updatedKnowledge,
        scope: sourceKnowledge.scope,
        deleted: updatedKnowledge.operate === 'DELETE' || updatedKnowledge.operate === 'DISABLE',
      }
    : modify
    ? sourceKnowledge
    : undefined;

  const { t, getKnowledgeContent } = useGetKnowledgeContent();

  const { mutateAsync: getTempUrlAsync, isLoading } = useMutation({
    mutationFn: async () => {
      const res = await getSourceKnowledgeUrl(sourceName);
      return res.data?.data;
    },
  });

  const handleOpenSourceUrl = async () => {
    if (isLoading) {
      return;
    }
    const tempUrl = await getTempUrlAsync();
    if (!tempUrl) {
      return;
    }
    openExternalURL(tempUrl);
  };

  const ref = useRef<HTMLDivElement>(null);

  const isSelf = Boolean(
    knowledgeType === 'supplemental_knowledge' &&
      knowledgeId &&
      caseId &&
      `${knowledgeId}` === `${caseId}`,
  );

  const [highlight, setHighlight] = useState(false);

  useEffect(() => {
    const handler = () => {
      if (isSelf) {
        setHighlight(true);
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };
    eventBus.on('HIGHLIGHT_CURRENT_QAPAIR_KNOWLEDGE', handler);
    return () => eventBus.off('HIGHLIGHT_CURRENT_QAPAIR_KNOWLEDGE', handler);
  }, [isSelf]);

  return getKnowledgeContent(
    {
      ...knowledge,
      scope: sourceKnowledge.scope,
      deleted: curKnowledge ? curKnowledge.deleted : deleted,
    },
    (title, content, items) => {
      const scoreColor =
        score <= 40
          ? 'var(--fett-surface-error-text)'
          : score <= 80
          ? '#FFC629'
          : score < 100
          ? '#3366FF'
          : 'var(--fett-scenario-success-text)';

      const getEditButton = () => {
        if (!onEdit) {
          return null;
        }

        const tips =
          deleted || curKnowledge?.deleted
            ? t('当前知识不可编辑')
            : !isNewProduct && source === '商家外部知识导入'
            ? t('当前知识不可修改，请修改文档后重新导入')
            : !isNewProduct && source === '商家线上文档同步'
            ? t('当前知识不可修改，请前往线上文档进行修改')
            : '';

        if (tips) {
          return (
            <Tooltip placement="topRight" title={tips}>
              <Button disabled type="text" icon={<EditFilled />} />
            </Tooltip>
          );
        }

        return <Button type="text" icon={<EditFilled />} onClick={onEdit} />;
      };

      const getDeleteButton = () => {
        if (!onDelete) {
          return null;
        }

        if (stopOperation) {
          return (
            <Tooltip placement="topRight" title={t('停用当前知识')}>
              <Button
                disabled={Boolean(deleted || disabled || curKnowledge?.deleted)}
                type="text"
                icon={<MinusCircleFilled />}
                onClick={onDelete}
              />
            </Tooltip>
          );
        }

        const tips =
          deleted || curKnowledge?.deleted || source === '商品详情页'
            ? t('当前知识不可删除')
            : !isNewProduct && source === '商家外部知识导入'
            ? t('当前知识不可删除，请修改文档后重新导入')
            : !isNewProduct && source === '商家线上文档同步'
            ? t('当前知识不可删除，请前往线上文档进行修改')
            : '';

        if (tips) {
          return (
            <Tooltip placement="topRight" title={tips}>
              <Button disabled type="text" icon={<DeleteFilled />} />
            </Tooltip>
          );
        }

        return <Button type="text" icon={<DeleteFilled />} onClick={onDelete} />;
      };

      // 新版商品知识编辑直存，不存在"待保存"的暂存态，changed 只说明引用快照来自保存前的旧版本
      const tag = changed && !isNewProduct ? (
        <Tag color="#FAAD14">{t('待保存')}</Tag>
      ) : deleted ? (
        <Tag color="var(--fett-surface-error-text)">
          {knowledgeType === 'supplemental_knowledge' ? t('已停用') : t('已删除')}
        </Tag>
      ) : disabled ? (
        <Tag color="#777">{t('已停用')}</Tag>
      ) : updatedKnowledge?.operate === 'DISABLE' ? (
        <Tag color="#777">{t('当前不引用')}</Tag>
      ) : updatedKnowledge?.operate === 'DELETE' ? (
        <Tag color="#FAAD14">{t('待保存')}</Tag>
      ) : null;

      return (
        <Container ref={ref} style={highlight ? { borderColor: '#336FFF' } : undefined}>
          {tag}
          <section>
            <header>
              {title}
              <div>
                {getEditButton()}
                {getDeleteButton()}
              </div>
            </header>
            {content}
            {!isNewProduct && (source === '商品详情页' || source === '商品详情页_人工修改') && (
              <p>
                <T
                  keyName="来源于x"
                  params={{
                    x: (
                      <TransComponent
                        content={
                          <span onClick={() => openExternalURL(sourceUrl)}>{sourceUrl}</span>
                        }
                      />
                    ),
                  }}
                />
              </p>
            )}
            {!isNewProduct && source === '商家线上文档同步' && (
              <p>
                <T
                  keyName="来源于x"
                  params={{
                    x: (
                      <TransComponent
                        content={
                          <span onClick={() => openExternalURL(sourceUrl)}>{sourceName}</span>
                        }
                      />
                    ),
                  }}
                />
              </p>
            )}
            {!isNewProduct && source === '商家外部知识导入' && (
              <p>
                <T
                  keyName="来源于x"
                  params={{
                    x: (
                      <TransComponent
                        content={<span onClick={handleOpenSourceUrl}>【{sourceName}】</span>}
                      />
                    ),
                  }}
                />
              </p>
            )}
          </section>
          <ul>
            <dl>
              <dt>{t('关联度')}：</dt>
              <dd className="flex items-center h-[22px]">
                <Progress
                  className="!mt-[-3px]"
                  showInfo={false}
                  percent={score}
                  strokeColor={scoreColor}
                />
                <span>{formatNumber(score / 100, '0.00%')}</span>
              </dd>
            </dl>
            <dl>
              <dt>{t('来源')}：</dt>
              <dd>
                {isNewProduct
                  ? knowledgeType === 'spu_detail_knowledge'
                    ? t('productKnowledge_knowledge_type_product_detail')
                    : t('productKnowledge_knowledge_type_document')
                  : (
                      {
                        客服提交答案: t('客服提交答案'),
                        AI运营_商品知识_商品属性: `${t('AI运营-菜单名称')} - ${t(
                          '商品知识-菜单名称',
                        )} - ${t('商品属性')}`,
                        商品详情页: t('商品详情页'),
                        商品详情页_人工修改: `${t('商品详情页')} - ${t('人工修改')}`,
                        商家外部知识导入: t('外部知识导入'),
                        商家线上文档同步: t('商家线上文档同步'),
                        主管提交历史未回复问题答案: t('主管提交历史未回复问题答案'),
                        CIH提交历史未回复问题答案: t('CIH提交历史未回复问题答案'),
                      } as Record<string, string>
                    )[source] ||
                    (knowledgeType === 'supplemental_knowledge'
                      ? t('productKnowledge_knowledge_type_reception')
                      : source)}
              </dd>
            </dl>
            {isNewProduct ? (
              <dl>
                <dt>{t('knowledge.应用商品')}：</dt>
                <dd>
                  <CitationScope scope={sourceKnowledge.scope} />
                </dd>
              </dl>
            ) : (
              items
            )}
            {time ? (
              <dl>
                <dt>
                  {(
                    {
                      客服提交答案: t('更新时间'),
                      AI运营_商品知识_商品属性: t('更新时间'),
                      商品详情页: t('同步时间'),
                      商品详情页_人工修改: t('修改时间'),
                      商家外部知识导入: t('同步时间'),
                      商家线上文档同步: t('同步时间'),
                      主管提交历史未回复问题答案: t('更新时间'),
                      CIH提交历史未回复问题答案: t('更新时间'),
                    } as Record<string, string>
                  )[source] || t('更新时间')}
                  ：
                </dt>
                {(() => {
                  const timeFormat = dayjs(time).format('YYYY-MM-DD HH:mm:ss');
                  const text =
                    source === '客服提交答案'
                      ? `${timeFormat} ${t('x提交', { x: updateUser })}`
                      : source === '商品详情页_人工修改'
                      ? `${timeFormat} ${t('x修改', { x: updateUser })}`
                      : timeFormat;
                  return (
                    <dd className="overflow-hidden text-ellipsis whitespace-nowrap" title={text}>
                      {text}
                    </dd>
                  );
                })()}
              </dl>
            ) : (
              <dl />
            )}
          </ul>
          {curKnowledge &&
            !curKnowledge.deleted &&
            getKnowledgeContent(
              curKnowledge,
              (title, content, items) => {
                return (
                  <div>
                    <section>
                      {tag ? null : updatedKnowledge ? (
                        <span>
                          <Tag color="#FAAD14" style={{ position: 'unset' }}>
                            {t('待保存')}
                          </Tag>
                          <div>{t('该知识已修改')}</div>
                        </span>
                      ) : (
                        <span>
                          <Tag
                            color="var(--fett-scenario-accent-text)"
                            style={{ position: 'unset' }}
                          >
                            {t('已更新')}
                          </Tag>
                          <div>
                            {t('该知识已于time更新', {
                              time: dayjs(sourceKnowledge.updateTime).format('YYYY-MM-DD HH:mm:ss'),
                            })}
                          </div>
                        </span>
                      )}
                      {title}
                      {content}
                    </section>
                    {items}
                  </div>
                );
              },
              false,
              index,
              translatedMap,
            )}
        </Container>
      );
    },
    isSelf,
    index,
    translatedMap,
  );
}

const Container = styled.section`
  position: relative;
  border: solid 1px var(--fett-border-strong);
  border-radius: 2px;
  > span {
    position: absolute;
    top: -9px;
    left: -1px;
    padding: 2px 10px;
    font-size: 12px;
    line-height: 14px;
    border-radius: 9px 0 12px;
    color: #fff;
  }
  > section {
    padding: 4px 10px 12px;
    > header {
      display: flex;
      align-items: center;
      margin-bottom: 4px;
      > h3 {
        flex: 1 0 0;
        margin-bottom: 0;
        font-size: 13px;
        font-weight: normal;
        color: var(--fett-text-tertiary);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        > span {
          color: var(--ant-primary-color);
        }
      }
      > div > button {
        color: #000;
        opacity: 0.6;
        &.ant-btn[disabled] {
          opacity: 0.25;
        }
      }
    }
    > p {
      margin-bottom: 0;
      margin-top: 10px;
      font-size: 12px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      > span {
        text-decoration: underline;
        color: var(--ant-primary-color);
        cursor: pointer;
      }
    }
  }
  > ul {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin: 0;
    padding: 12px 10px;
    border-top: dashed 1px var(--fett-border-strong);
    > dl {
      flex: 0 0 calc(50% - 6px);
      display: flex;
      margin-bottom: 0;
      overflow: hidden;
      > dt {
        flex: 0 0 auto;
        font-weight: normal;
      }
      > dd {
        flex: 1 0 0;
        margin-bottom: 0;
      }
    }
  }
  > div {
    background: #f4f8ff;
    > section {
      padding: 10px 12px;
      border-bottom: dashed 1px var(--fett-border-strong);
      > span {
        display: flex;
        align-items: center;
        margin-bottom: 12px;
        > span {
          margin-right: 8px;
        }
        > div {
          flex: 1 0 0;
          color: #007aff;
        }
      }
      > h3 {
        margin-bottom: 6px;
        font-size: 13px;
        font-weight: normal;
        color: var(--fett-text-tertiary);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        > span {
          display: none;
        }
      }
    }
    > dl {
      display: flex;
      margin: 0;
      padding: 10px 12px;
      > dt {
        font-weight: normal;
      }
      > dd {
        margin-bottom: 0;
      }
    }
  }
`;

/* UED 暗色 Tag 映射：角标在 light 为实心底白字，dark 改为同色系填充浅色型（透明底+彩色字） */
const tagDarkToneMap: Record<string, 'orange' | 'red' | 'gray' | 'blue'> = {
  '#FAAD14': 'orange',
  'var(--fett-surface-error-text)': 'red',
  '#777': 'gray',
  'var(--fett-scenario-accent-text)': 'blue',
};

const Tag = styled.span<{ color: string }>`
  position: absolute;
  top: -9px;
  left: -1px;
  padding: 2px 10px;
  font-size: 12px;
  line-height: 14px;
  border-radius: 9px 0 12px;
  color: #fff;
  background-color: ${({ color }) => color};
  [data-color-mode='dark'] & {
    color: ${({ color }) => `var(--fett-tag-${tagDarkToneMap[color] || 'gray'}-text)`};
    background-color: ${({ color }) => `var(--fett-tag-${tagDarkToneMap[color] || 'gray'}-bg)`};
  }
`;

const Text = styled.div`
  font-size: 14px;
  font-weight: bold;
  line-height: 17px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  b {
    display: inline-block;
    padding: 0 4px;
    margin-right: 4px;
    height: 18px;
    line-height: 18px;
    font-size: 12px;
    font-weight: normal;
    color: #fff;
    background: #336fff;
    border-radius: 2px;
  }
`;

const Media = styled.div`
  display: flex;
  margin-top: 4px;
  > * {
    margin-right: 4px;
  }
`;

export default Knowledge;
